import {
 Button, IconButton, SxProps, Theme,
} from '@mui/material';
import React, { useState } from 'react';
import ShareMenu from './SharedAlbum/ShareMenu';

interface AlbumShareButtonProps {
  // Text label for the button. Required when `icon` is not provided.
  label?: string;
  // When provided, renders a square IconButton instead of a text Button.
  icon?: React.ReactNode;
  // The album's public share URL. Should already be an absolute URL when used
  // outside the share page so social/native sharing carries the correct origin.
  url: string;
  // Mobile: tap opens the OS share sheet. Desktop: tap opens the custom Copy
  // Link / Facebook popover.
  useNativeShare?: boolean;
  onCopySuccess: () => void;
  buttonSx?: SxProps<Theme>;
  ariaLabel?: string;
  // Optional analytics hooks. Undefined on the public share page (no tracking there).
  onShareClick?: () => void;
  onCopyClick?: () => void;
  onFacebookClick?: () => void;
}

function AlbumShareButton({
  label,
  icon,
  url,
  useNativeShare,
  onCopySuccess,
  buttonSx,
  ariaLabel,
  onShareClick,
  onCopyClick,
  onFacebookClick,
}: AlbumShareButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    onShareClick?.();
    if (useNativeShare && typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
      } catch (err) {
        // user cancelled — silent
      }
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      {icon ? (
        <IconButton
          onClick={handleClick}
          aria-label={ariaLabel || 'share'}
          sx={buttonSx}
        >
          {icon}
        </IconButton>
      ) : (
        <Button variant="contained" onClick={handleClick} sx={buttonSx}>
          {label}
        </Button>
      )}
      {!useNativeShare && (
        <ShareMenu
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          url={url}
          onCopySuccess={onCopySuccess}
          onCopyClick={onCopyClick}
          onFacebookClick={onFacebookClick}
        />
      )}
    </>
  );
}

export default AlbumShareButton;
